/**
 * useGeoSearch — debounced city lookup for the Place-of-Birth overlay.
 *
 * Behaviour mirrors the Android ViewModel:
 *  - empty query → fetch the default top cities (fires immediately on mount)
 *  - 1–2 chars   → clear results (too short to search)
 *  - ≥3 chars    → debounce 300ms, then fetch
 *  - in-flight requests are aborted when the query changes or on unmount
 *  - on API failure with a usable query, `failed` lets the UI offer the typed
 *    text as a fallback place.
 *
 * The hook is meant to live only while the overlay is open (mount it with the
 * overlay), so its state resets cleanly each time the overlay is reopened.
 */
import { useEffect, useRef, useState } from 'react'
import { getGeoDetails } from '../../api/astroForm'

const DEBOUNCE_MS = 300
export const MIN_CHARS = 3

export interface GeoSearchState {
  query: string
  setQuery: (q: string) => void
  results: string[]
  loading: boolean
  /** Last fetch errored — caller can offer the typed query as a fallback. */
  failed: boolean
}

export function useGeoSearch(): GeoSearchState {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const q = query.trim()

    // Too short to search (but not empty): clear and bail.
    if (q.length > 0 && q.length < MIN_CHARS) {
      abortRef.current?.abort()
      setResults([])
      setLoading(false)
      setFailed(false)
      return
    }

    setLoading(true)
    setFailed(false)
    // Empty query (top cities) fires immediately; typed queries debounce.
    const delay = q.length === 0 ? 0 : DEBOUNCE_MS
    const timer = setTimeout(() => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      getGeoDetails(q, controller.signal)
        .then((cities) => {
          if (controller.signal.aborted) return
          setResults(cities)
          setLoading(false)
        })
        .catch(() => {
          if (controller.signal.aborted) return
          setResults([])
          setLoading(false)
          setFailed(true)
        })
    }, delay)

    // Cancel the pending debounce AND any request already in flight, so a stale
    // response for the previous query can't land after it changed.
    return () => {
      clearTimeout(timer)
      abortRef.current?.abort()
    }
  }, [query])

  // Abort any in-flight request when the hook unmounts (overlay closed).
  useEffect(() => () => abortRef.current?.abort(), [])

  return { query, setQuery, results, loading, failed }
}
